import React, { useState, useRef, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { majorAirports, type Airport } from "@/lib/airports";
import { MapPin, Plane } from "lucide-react";

interface AirportSearchProps {
  value: string;
  onChange: (code: string) => void;
  placeholder?: string;
  className?: string;
}

export function AirportSearch({ 
  value, 
  onChange, 
  placeholder = "Search city or airport...",
  className 
}: AirportSearchProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  // Filter airports by search term (city, name, state, or code)
  const filteredAirports = React.useMemo(() => {
    if (!search || search.length < 2) return [];
    
    const term = search.toLowerCase().trim();
    
    return majorAirports
      .filter((airport) => {
        return (
          airport.code.toLowerCase().includes(term) ||
          airport.city.toLowerCase().includes(term) ||
          airport.name.toLowerCase().includes(term) ||
          airport.state.toLowerCase().includes(term)
        );
      })
      .slice(0, 10); // Limit to 10 results
  }, [search]);

  // Update search when external value changes
  useEffect(() => {
    if (value && value !== search) {
      setSearch(value);
    }
  }, [value]);

  const handleSelect = (airport: Airport) => {
    setSearch(airport.code);
    onChange(airport.code);
    setOpen(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value.toUpperCase();
    setSearch(newValue);
    
    // If it's a valid 3-letter code, update immediately
    if (newValue.length === 3 && majorAirports.some(a => a.code === newValue)) {
      onChange(newValue);
    } else if (newValue.length === 0) {
      onChange('');
    }
    
    // Open popover when typing
    if (newValue.length >= 2) {
      setOpen(true);
    }
  };

  const handleBlur = () => {
    // On blur, if the input is a valid code, keep it; otherwise clear
    const upperSearch = search.toUpperCase();
    const matchedAirport = majorAirports.find(a => a.code === upperSearch);
    if (matchedAirport) {
      setSearch(matchedAirport.code);
      onChange(matchedAirport.code);
    }
    // Delay close to allow click selection
    setTimeout(() => setOpen(false), 150);
  };

  const formatLocation = (airport: Airport): string => {
    if (airport.country === 'US') {
      return `${airport.city}, ${airport.state}`;
    }
    return `${airport.city}, ${airport.country}`;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div className={className}>
          <Input
            ref={inputRef}
            value={search}
            onChange={handleInputChange}
            onFocus={() => search.length >= 2 && setOpen(true)}
            onBlur={handleBlur}
            placeholder={placeholder}
            className="font-mono text-xs w-full"
          />
        </div>
      </PopoverTrigger>
      <PopoverContent 
        className="p-0 w-[300px]" 
        align="start"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <Command>
          <CommandList>
            {filteredAirports.length === 0 && search.length >= 2 && (
              <CommandEmpty className="py-3 text-center text-xs text-muted-foreground">
                No airports found for "{search}"
              </CommandEmpty>
            )}
            {filteredAirports.length > 0 && (
              <CommandGroup>
                {filteredAirports.map((airport) => (
                  <CommandItem
                    key={airport.code}
                    value={`${airport.code}-${airport.city}-${airport.name}`}
                    onSelect={() => handleSelect(airport)}
                    className="cursor-pointer"
                  >
                    <div className="flex items-start gap-2 w-full">
                      <Plane className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-bold text-sm">{airport.code}</span>
                          <span className="text-xs text-muted-foreground truncate">
                            {airport.name}
                          </span>
                        </div>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <MapPin className="h-3 w-3" />
                          <span>{formatLocation(airport)}</span>
                        </div>
                      </div>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
